// ---------------------------------------------------------------------------
// Typed fetch wrapper for non-streaming qm-api endpoints (spec §8.1–§8.4).
// Streaming is handled separately by streamingAbsence.ts (§8.5 NDJSON).
//
// In dev, requests go through Vite's `/api` proxy (vite.config.ts) which
// targets VITE_API_BASE_URL (defaults to http://localhost:8000). In prod the
// frontend is served same-origin with the API, so absolute URLs aren't needed.
//
// Auth (spec §15.3): JWT access token (30-min, in-memory) + refresh token
// (7-day, localStorage). On 401 the request silently swaps the refresh token
// for a fresh access token and retries once before failing. If the refresh
// also fails, tokens are cleared and the user is redirected to /login.
//
// Error mapping: every non-2xx response is parsed as the §8.0 envelope
// `{ error: { code, message, request_id, details } }` and thrown as ApiError.
// ---------------------------------------------------------------------------

import { ApiError } from "./errors";
import type { TokenResponse } from "./types";

export type RequestOptions = Omit<RequestInit, "body"> & {
  params?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
};

// ----- Token state (spec §15.3) --------------------------------------------
// access_token held in memory only — closing the tab clears it. Not in
// localStorage to reduce XSS surface. refresh_token held in localStorage so
// it survives reload; the in-memory copy mirrors it for fast access.

let accessToken: string | null = null;
let refreshToken: string | null = null;

export function setTokens(access: string, refresh: string): void {
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem("qm_refresh_token", refresh);
}

export function clearTokens(): void {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem("qm_refresh_token");
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function loadRefreshToken(): string | null {
  return localStorage.getItem("qm_refresh_token");
}

// ----- Refresh helper (spec §15.3) -----------------------------------------
// Swaps the stored refresh token for a fresh access+refresh pair. Returns
// true on success; on failure clears tokens and returns false. Called by
// request() when a request comes back with 401.

async function refreshAccessToken(): Promise<boolean> {
  const stored = refreshToken ?? loadRefreshToken();
  if (!stored) return false;

  try {
    const url = buildUrl("/api/v1/auth/refresh");
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: stored }),
    });
    if (!response.ok) {
      clearTokens();
      return false;
    }
    const data = (await response.json()) as TokenResponse;
    setTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    clearTokens();
    return false;
  }
}

/** POST /api/v1/auth/refresh using the stored refresh token (bootstrap + 401 retry path). */
export async function refreshTokensFromStorage(): Promise<boolean> {
  return refreshAccessToken();
}

// ----- URL builder ---------------------------------------------------------

export const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

function buildUrl(path: string, params?: RequestOptions["params"]): string {
  const fullPath = path.startsWith("/") ? `${API_BASE}${path}` : `${API_BASE}/${path}`;
  if (!params) return fullPath;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${fullPath}?${qs}` : fullPath;
}

// ----- Core request fn with 401 silent-refresh -----------------------------

async function request<T>(
  method: string,
  path: string,
  options?: RequestOptions,
  isRetry = false,
): Promise<T> {
  const url = buildUrl(path, options?.params);
  const headers = new Headers(options?.headers);
  const token = getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // multipart uploads: let the browser set Content-Type (with the boundary)
  // and send the FormData as-is rather than JSON-encoding it.
  const isForm = options?.body instanceof FormData;
  if (options?.body !== undefined && !isForm && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const init: RequestInit = {
    ...options,
    method,
    headers,
    body:
      options?.body === undefined
        ? undefined
        : isForm
          ? (options.body as FormData)
          : JSON.stringify(options.body),
  };
  delete (init as { params?: unknown }).params;

  const response = await fetch(url, init);

  // Spec §15.3: on 401, attempt a silent refresh once, then retry.
  // If refresh also fails, clear tokens and redirect to /login.
  if (response.status === 401 && !isRetry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return request<T>(method, path, options, true);
    }
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const body = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw ApiError.fromResponse(response.status, body);
  }
  return body as T;
}

export const api = {
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>("GET", path, options);
  },
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>("POST", path, { ...options, body });
  },
  /** Multipart POST — sends FormData without setting Content-Type so the
   * browser supplies the boundary; JWT is attached like every other call. */
  postForm<T>(path: string, form: FormData, options?: RequestOptions): Promise<T> {
    return request<T>("POST", path, { ...options, body: form });
  },
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>("PUT", path, { ...options, body });
  },
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>("PATCH", path, { ...options, body });
  },
  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>("DELETE", path, options);
  },
};
